import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight } from 'lucide-react';
import { useQuickToolPrices } from '@/components/quicktools/QuickToolCards';
import { CoverExamples, useCoverExamples } from '@/components/quicktools/CoverExamples';
import { PhoneCompare } from '@/components/quicktools/PhoneCompare';
import { CanvasAppsLine, CanvasByline } from '@/components/quicktools/CanvasLines';
import { money } from '@/lib/quicktools-api';
import { ShareToolBar } from '@/components/share/ShareToolBar';
import { trackEvent } from '@/lib/analytics';

// THE LIVING ALBUM COVER ON THE LANDING PAGE (owner 2026-10-16: "this should
// definitely be advertised on the landing page because those cool images
// will catch eyes"). The examples are the ones the owner makes himself and
// picks (owner 2026-10-17: "i dont want them all jammed in together and
// shrunk down ... they need to be spread out and allowing for the full cover
// to be displayed"): each one big and live under the pitch. Until he has
// picked any, the band is the pitch and the button.

/** `embedded`: inside the studio chooser (no page padding of its own). */
export function SpotifyLoopBand({ embedded = false }: { embedded?: boolean } = {}) {
  const { t } = useTranslation();
  const price = money(useQuickToolPrices().canvas);
  const [examples, setExamples] = useCoverExamples();
  return (
    <section aria-labelledby="spotify-loop" data-testid="home-spotify-loop" className={embedded ? 'mb-10 rounded-3xl border border-white/10 bg-white/[0.02] px-4 py-8 sm:mb-14 sm:px-8 sm:py-12' : 'py-10 sm:py-16'}>
      <div className={embedded ? 'mx-auto grid max-w-6xl items-center gap-8 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)]' : 'container mx-auto max-w-6xl px-4 md:px-8 xl:px-4'}>
        <div className={embedded ? 'text-center lg:text-left' : 'mx-auto max-w-2xl text-center'}>
          <p className="eyebrow">{t('home.loopEyebrow')}</p>
          {/* The same apps line, title and byline as /canvas (owner 2026-10-20). */}
          <CanvasAppsLine className="mt-3" />
          <h2 id="spotify-loop" className="mt-2 text-balance font-display text-[1.9rem] font-bold leading-[1.05] sm:text-[2.6rem]">
            {/* No "Living Cover." in front (owner 2026-10-20: "you can ditch the living cover the beginning"). */}
            <span className="gradient-text">{t('qt.tools.canvas.h1b')}</span>
          </h2>
          <CanvasByline className="mt-2" />
          <p className="mt-3 text-pretty text-[15px] leading-relaxed text-muted-foreground sm:text-[1.05rem]">{t('home.loopLead')}</p>
          <p className="mt-3 text-pretty text-[15px] font-semibold leading-relaxed text-foreground sm:text-[1.05rem]" data-testid="home-loop-dedicate">{t('home.loopDedicate')}</p>
          <Link
            to="/canvas"
            onClick={() => trackEvent('home_spotify_loop_clicked', {})}
            data-testid="home-spotify-loop-cta"
            className="mt-6 inline-flex min-h-[52px] items-center gap-2 rounded-full bg-[linear-gradient(135deg,#F9D27E,#F4BE52_45%,#E4A43A)] px-6 text-[16px] font-bold text-[#10131a] shadow-[0_10px_30px_-10px_rgba(244,190,82,0.7)]"
          >
            {t('home.loopCta', { price })}
            <ArrowRight className="h-4 w-4" aria-hidden />
          </Link>
          <ShareToolBar path="/canvas" text={t('home.loopShareText')} align="center" className={embedded ? 'mt-5 lg:items-start' : 'mt-5'} />
        </div>
        {/* In the studio chooser: the without-and-with phones, every example, in place of the one swipeable example
            (owner 2026-10-20: "cut out this entire promo piece and integrate it into the landing page promo block"). */}
        {embedded && examples && examples.length > 0 && <PhoneCompare examples={examples} className="max-sm:-mx-4 max-sm:w-auto" />}
        {!embedded && examples && examples.length > 0 && <PhoneCompare examples={examples} className="mt-10" />}
        {!embedded && examples && examples.length > 0 && (
          <CoverExamples examples={examples} onRemoved={(id) => setExamples((x) => (x || []).filter((e) => e.id !== id))} className="mt-10" />
        )}
      </div>
    </section>
  );
}
